import { CartHeader } from '../../../pages/cartItems/CartHeader';

const SkeletonPulse = ({ className }) => (
  <div className={`bg-gray-200 animate-pulse rounded ${className}`}></div>
);

const SkeletonText = ({ width = 'w-full', height = 'h-4' }) => (
  <SkeletonPulse className={`${width} ${height}`} />
);

const SkeletonProductCard = () => (
  <div className="flex gap-4 p-4 border border-gray-100 rounded-lg mb-3">
    <SkeletonPulse className="w-20 h-20 rounded-md flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <SkeletonText width="w-3/4" />
      <SkeletonText width="w-1/2" />
      <SkeletonText width="w-1/4" height="h-5" />
    </div>
  </div>
);

const SkeletonAddressCard = () => (
  <div className="border border-gray-200 rounded-lg p-4 mb-4">
    <div className="flex items-start gap-3">
      <SkeletonPulse className="w-5 h-5 rounded-full flex-shrink-0 mt-1" />
      <div className="flex-1 space-y-2">
        <SkeletonText width="w-1/3" height="h-5" />
        <SkeletonText width="w-2/3" />
        <SkeletonText width="w-1/2" />
      </div>
    </div>
  </div>
);

const SkeletonDeliveryOption = () => (
  <div className="border border-gray-200 rounded-lg p-4 mb-3">
    <div className="flex items-center gap-3">
      <SkeletonPulse className="w-5 h-5 rounded-full flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <SkeletonText width="w-1/4" height="h-5" />
        <SkeletonText width="w-1/2" />
      </div>
      <SkeletonText width="w-16" height="h-5" />
    </div>
  </div>
);

const SkeletonPaymentMethod = () => (
  <div className="border border-gray-200 rounded-lg p-4 mb-3">
    <div className="flex items-center gap-3">
      <SkeletonPulse className="w-10 h-7 rounded flex-shrink-0" />
      <SkeletonText width="w-1/3" height="h-5" />
    </div>
  </div>
);

const SkeletonSummaryRow = () => (
  <div className="flex justify-between items-center py-2">
    <SkeletonText width="w-1/4" />
    <SkeletonText width="w-1/5" />
  </div>
);

const Divider = ({ className = '' }) => (
  <div className={`border-t-2 border-gray-100 w-full my-4 ${className}`}></div>
);

const CheckoutItemsSkeleton = () => {
  return (
    <main className="flex flex-col min-h-screen">
      <CartHeader />
      <hr className="mt-2 mb-8 hidden lg:block" />

      <section className="grid lg:grid-cols-[60%_40%] lg:px-[76px] flex-1">
        {/* Main Content */}
        <main className="w-full pt-8 lg:px-8">
          {/* Mobile Product Card */}
          <section className="lg:hidden px-4">
            <SkeletonProductCard />
          </section>

          <Divider className="lg:hidden" />

          {/* Delivery Address Section */}
          <div className="px-6 lg:px-0 pt-4">
            <div className="flex justify-between items-center mb-4">
              <SkeletonText width="w-40" height="h-6" />
              <SkeletonText width="w-16" height="h-4" />
            </div>
            <SkeletonAddressCard />
            <SkeletonAddressCard />
          </div>

          {/* Delivery Options Section */}
          <section className="mt-8 px-6 lg:px-0">
            <SkeletonText width="w-36" height="h-6" />
            <div className="mt-4">
              <SkeletonDeliveryOption />
              <SkeletonDeliveryOption />
            </div>
          </section>

          {/* Payment Method Section - Desktop */}
          <section className="mt-8 hidden lg:block">
            <div className="mb-4">
              <SkeletonText width="w-40" height="h-6" />
              <SkeletonText width="w-48" height="h-4" className="mt-2" />
            </div>
            <SkeletonPaymentMethod />
            <SkeletonPaymentMethod />
            <SkeletonPaymentMethod />
          </section>
        </main>

        {/* Sidebar */}
        <aside className="border-l-2 border-gray-100 pt-8 lg:bg-[#FAFAFA] lg:pr-3 h-full">
          {/* Review Order Header - Desktop */}
          <div className="hidden lg:block pl-8 pt-6 mb-4">
            <SkeletonText width="w-36" height="h-6" />
          </div>

          {/* Desktop Product Card */}
          <div className="hidden lg:block px-4">
            <SkeletonProductCard />
            <SkeletonProductCard />
          </div>

          {/* Order Summary - Desktop */}
          <div className="hidden lg:block px-6 mt-4">
            <div className="bg-white rounded-lg p-4 space-y-1">
              <SkeletonSummaryRow />
              <SkeletonSummaryRow />
              <SkeletonSummaryRow />
              <Divider />
              <div className="flex justify-between items-center py-2">
                <SkeletonText width="w-1/3" height="h-5" />
                <SkeletonText width="w-1/4" height="h-5" />
              </div>
            </div>
          </div>

          <Divider className="lg:hidden" />

          {/* Payment Method - Mobile */}
          <section className="lg:hidden px-4">
            <SkeletonPaymentMethod />
            <SkeletonPaymentMethod />
          </section>

          <Divider className="lg:hidden" />

          {/* Payment Summary */}
          <div className="px-4 lg:px-6 py-4">
            <div className="bg-white lg:bg-transparent rounded-lg p-4 space-y-3">
              <SkeletonSummaryRow />
              <SkeletonSummaryRow />
              <Divider />
              <div className="flex justify-between items-center">
                <SkeletonText width="w-1/3" height="h-5" />
                <SkeletonText width="w-1/4" height="h-6" />
              </div>
              <SkeletonPulse className="w-full h-12 rounded-lg mt-4" />
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
};

export default CheckoutItemsSkeleton;
